// src/pages/Loans.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Table, { Column } from "../../components/Table";
import Button from "../../components/Button";
import { listLoans, LoanItem } from "../../services/loanService";
import { useNavigate } from "react-router-dom";
import InputField from "../../components/InputField";
import { Icon } from "@iconify/react";
import axiosClient from "../../api/axiosClient";

type LoanRow = {
  id: string | number;
  profile?: string | null;
  name: string;
  code?: string | null;
  loanId: string;
  registration: string;
  status: string;
  raw: LoanItem;
  loan_code?: string | number;
};

export default function Loans() {
  const [data, setData] = useState<LoanRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // server pagination state
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(1);

  // search state
  const [searchTerm, setSearchTerm] = useState<string>(""); // controlled input
  const [debouncedSearch, setDebouncedSearch] = useState<string>(""); // debounced value
  const debounceRef = useRef<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [selectedCount, setSelectedCount] = useState<number | null>(null);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);


  const navigate = useNavigate();

  // Debounce input -> set debouncedSearch after 500ms
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 500);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // whenever debouncedSearch changes, reset to page 1
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchLoans = async (
    p = page,
    per_page = pageSize,
    query = debouncedSearch
  ) => {
    try {
      setLoading(true);
      setError(null);

      const useSearchEndpoint =
        (query && query.length > 0) || statusFilter !== "All";

      let items: LoanRow[] = [];
      let meta: any = {};

      if (useSearchEndpoint) {
        const params: any = {
          page: p,
          per_page,
        };

        if (query) params.query = query;
        if (statusFilter !== "All") params.status = statusFilter;

        const res = await axiosClient.get("/loans/search", { params });
        const payload = res.data ?? {};

        items = (payload.data ?? []).map(normalizeRaw);

        const totalCount = Number(
          payload.count ?? payload.total ?? items.length
        );

        meta = {
          current_page: p,
          per_page,
          total: totalCount,
          last_page: Math.max(1, Math.ceil(totalCount / per_page)),
          from: items.length ? (p - 1) * per_page + 1 : 0,
          to: (p - 1) * per_page + items.length,
        };

        setSelectedCount(totalCount);
      } else {
        const r = await listLoans({ page: p, per_page });

        if (!r.success) {
          setError(r.message ?? "Failed to fetch loans");
          setData([]);
          return;
        }

        // ✅ TS SAFE NOW
        items = r.data.items.map(normalizeLoanItemToRow);
        meta = r.data.meta ?? {};

        setSelectedCount(Number(meta.total ?? items.length));
      }

      // ✅ Single source of truth
      setData(items);
      setPage(meta.current_page ?? p);
      setPageSize(meta.per_page ?? per_page);
      setTotal(meta.total ?? items.length);
      setLastPage(meta.last_page ?? 1);
      setFrom(meta.from ?? 0);
      setTo(meta.to ?? 0);

    } catch (err: any) {
      setError(err?.message || "Failed to fetch loans");
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    setPage(1);
  }, [statusFilter]);
  // normalize helpers — replace your existing functions with these
  function normalizeRaw(loan: any): LoanRow {
    const borrower = loan.borrower || {};
    // ensure we pick a stable id from all possible fields
    const resolvedId = loan.id ?? loan.loan_id ?? loan.loan_code ?? loan.loanCode ?? loan.loanId ?? null;

    return {
      // use resolvedId so row.id is always populated if any ID-like field exists
      id: resolvedId,
      profile: borrower.profile_photo_url,
      name: `${borrower.first_name ?? ""} ${borrower.last_name ?? ""}`.trim(),
      code: borrower.customer_code ?? null,
      loanId: `LN-${loan.loan_id ?? loan.id ?? loan.loan_code ?? "-"}`,
      loan_code: loan.loan_code ?? loan.loan_id ?? loan.id,
      registration: loan.vehicle?.registration_number ?? "N/A",
      // normalise status to a canonical lowercase string (makes comparisons easier)
      status: typeof loan.status === "string" ? loan.status.toLowerCase() : String(loan.status ?? "unknown"),
      raw: loan,
    } as LoanRow;
  }

  function normalizeLoanItemToRow(raw: LoanItem): LoanRow {
    const loan = raw as any;
    const borrower = loan.borrower || {};
    const resolvedId = loan.id ?? loan.loan_id ?? loan.loan_code ?? loan.loanCode ?? loan.loanId ?? null;

    return {
      id: resolvedId,
      profile: borrower.profile_photo_url,
      name: `${borrower.first_name ?? ""} ${borrower.last_name ?? ""}`.trim(),
      code: borrower.customer_code ?? null,
      loanId: `LN-${loan.loan_id ?? loan.id ?? loan.loan_code ?? "-"}`,
      loan_code: loan.loan_code ?? loan.loan_id ?? loan.id,
      registration: loan.vehicle?.registration_number ?? "N/A",
      status: typeof loan.status === "string" ? loan.status.toLowerCase() : String(loan.status ?? "unknown"),
      raw: loan,
    } as LoanRow;
  }


  useEffect(() => {
    fetchLoans(page, pageSize, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statusFilter]);

  const columns: Column<LoanRow>[] = useMemo(
    () => [
      {
        header: "Customer",
        accessor: "name",
        sortable: true,
        cell: (row) => (
          <div className="flex items-center gap-3">
            <div className="relative">
            {/* {row.profile ? (
              <img src={row.profile} alt={row.name} className="w-10 h-10 rounded-full object-cover" />
            ) : ( */}
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500">
                <span className="text-white font-semibold">{row.name?.charAt(0)?.toUpperCase()}</span>
              </div>
            {/* )} */}

            {(row.raw.status === "Active" || row.raw.status === "Inactive") && (
              <span
                className={`absolute top-0 right-0 w-3 h-3 border-2 border-white rounded-full ${
                  row.raw.status === "Active" ? "bg-green-500" : "bg-red-500"
                }`}
              ></span>
            )}
          </div>
            <div>
              <div className="font-medium">{row.name}</div>
              {row.code && <div className="text-xs text-gray-400">{row.code}</div>}
            </div>
          </div>
        ),
      },
      {
        header: "Loan Code",
        accessor: "loan_code",
        sortable: true,
        cell: (row) => <div className="font-mono text-sm">{row.loan_code}</div>,
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        cell: (row) => (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.status === "active" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
            {row.status}
          </span>
        ),
      },
      {
        header: "Registration",
        accessor: "registration",
        sortable: true,
        cell: (row) => <div>{row.registration}</div>,
      },

    ],
    []
  );

  const handleNavigateToDues = (row: LoanRow) => {
    navigate("/loan-dues", { state: { loan_id: row.id } });
  };

  // helper: immediate search on Enter key
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // cancel debounce and apply immediately
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      setDebouncedSearch(searchTerm.trim());
    }
  };

  if (error && data.length === 0) return <div className="p-4 text-red-500">Error: {error}</div>;

  return (
    <div className="pt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 mb-4 gap-4 items-center bg-white p-4 rounded-xl shadow-sm">
        <div className="flex justify-between">
          <h2 className="text-2xl font-semibold">Loans</h2>

          <div className="flex items-center gap-2">
            {selectedCount !== null && (
              <span
                className="
      text-xs 
      font-medium 
      text-white 
      px-3 
      py-1 
      rounded-full 
      shadow-sm 
      flex 
      items-center 
      gap-1
      transition 
      bg-yellow-500
    "
              >
                <span className=""> count : </span>
                {selectedCount}
              </span>
            )}

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-200 shadow-sm rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:outline-none transition"
            >
              <option value="All">All</option>
              <option value="active">Active</option>
              <option value="inprogress">Inprogress</option>
              <option value="completed">Completed</option>
              <option value="settlement">Settlement</option>
            </select>

          </div>
        </div>


        <div className="">
          {/* Search Input */}
          <div className="relative w-full ">
            <InputField
              placeholder="(eg: CUS001 or LN012 or Name)"
              className="w-full focus:outline-none focus:ring-2 focus:ring-sky-300 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon icon="mdi:magnify" width={18} className="text-slate-400" />
            </div>
          </div>
        </div>

      </div>


      <Table<LoanRow>
        data={data}
        columns={columns}
        serverSide
        total={total}
        from={from}
        to={to}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        defaultPageSize={20}
        pageSizeOptions={[20]}
        loading={loading}
        onRowClick={(row) => handleNavigateToDues(row)}
      />

    </div>
  );
}
